import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Spin, Table, Segmented } from 'antd';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area
} from 'recharts';
import api from '../api';
import { useFilters } from '../contexts/FilterContext';
import { useTheme } from '../contexts/ThemeContext';
import FullscreenChart from './FullscreenChart';
import WorldMap from './WorldMap';
import '../responsive.css';

const { Title, Text } = Typography;

// Modern, elegant font styling for charts
const getChartFontStyle = (isDark: boolean) => ({
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  letterSpacing: '0.3px',
  fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#4B5563'
});

const getChartLabelStyle = (isDark: boolean) => ({
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.2px',
  fill: isDark ? 'rgba(255, 255, 255, 0.85)' : '#1F2937'
});

// Helper function to format revenue values conditionally
const formatRevenue = (value: number) => {
  if (value === 0) return '$0.0M';
  if (Math.abs(value) < 1.0) {
    return `$${value.toFixed(2)}M`;
  }
  return `$${value.toFixed(1)}M`;
};

// Helper function to format delta values conditionally
const formatDelta = (value: number) => {
  const sign = value >= 0 ? '+' : '';
  if (value === 0) return `${sign}$0.0M`;
  if (Math.abs(value) < 1.0) {
    return `${sign}$${value.toFixed(2)}M`;
  }
  return `${sign}$${value.toFixed(1)}M`;
};

interface RegionalAnalysisProps {
  data: any;
  loading: boolean;
}

const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];

// Consistent color mapping for regions - each region always gets the same color
const REGION_COLORS: { [key: string]: string } = {
  'North America': '#1890ff',      // Blue
  'Europe': '#52c41a',             // Green (matches the actual data from backend)
  'Asia Pacific': '#faad14',       // Orange
  'Middle East & Africa': '#f5222d', // Red
  'Latin & Central America': '#722ed1', // Purple
  'Eastern Europe': '#13c2c2',     // Cyan
  'Unknown': '#fa8c16'             // Dark Orange
};

const RegionalAnalysis: React.FC<RegionalAnalysisProps> = ({ data, loading }) => {
  const { isDark } = useTheme();
  const [regionalData, setRegionalData] = useState<any[]>([]);
  const [regionalGrowth, setRegionalGrowth] = useState<any[]>([]);
  const [marketShare, setMarketShare] = useState<any[]>([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [fullscreenChart, setFullscreenChart] = useState<string | null>(null);
  const [chartView, setChartView] = useState<'bars' | 'area'>('bars');
  const { filters } = useFilters();
  
  // Dynamic legend style based on theme
  const legendStyle = {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: '13px',
    fontWeight: 500,
    letterSpacing: '0.2px',
    color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#374151'
  };

  // Custom tooltip styling
  const CustomTooltip = ({ active, payload, isDark }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: isDark ? '#1f1f1f' : 'rgba(255, 255, 255, 0.98)',
          border: `1px solid ${isDark ? '#434343' : '#e5e7eb'}`,
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          fontSize: '14px',
          fontWeight: 500
        }}>
          <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#6b7280', marginBottom: '4px' }}>{payload[0].name}</div>
          <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#111827', fontSize: '16px', fontWeight: 600 }}>
            ${Number(payload[0].value).toFixed(1)}M
          </div>
          {payload[0].payload?.share && (
            <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#6b7280', fontSize: '12px', marginTop: '4px' }}>
              {payload[0].payload.share.toFixed(1)}% market share
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Helper function to get sorted regions by total value across all years
  const getSortedRegions = () => {
    if (!regionalData || regionalData.length === 0) return [];
    
    return Object.keys(regionalData[0] || {})
      .filter(key => key !== 'year')
      .sort((a, b) => {
        // Calculate total value for each region across all years
        const totalA = regionalData.reduce((sum, yearData) => sum + (yearData[a] || 0), 0);
        const totalB = regionalData.reduce((sum, yearData) => sum + (yearData[b] || 0), 0);
        // Sort in descending order (largest first)
        return totalB - totalA;
      });
  };

  // Helper function to get regions in reverse order for stacked area charts
  const getReversedRegions = () => {
    if (!regionalData || regionalData.length === 0) return [];
    
    return Object.keys(regionalData[0] || {})
      .filter(key => key !== 'year')
      .sort((a, b) => {
        // Calculate total value for each region across all years
        const totalA = regionalData.reduce((sum, yearData) => sum + (yearData[a] || 0), 0);
        const totalB = regionalData.reduce((sum, yearData) => sum + (yearData[b] || 0), 0);
        // Sort in descending order (largest first) for area stacking
        // This ensures largest regions appear at the bottom of the stack
        return totalB - totalA;
      });
  };


  const fetchRegionalData = React.useCallback(async () => {
    // Don't fetch if therapeutic area is not set yet
    if (!filters.therapeuticArea || filters.therapeuticArea === '') {
      console.log('Skipping fetch - therapeutic area not set yet');
      return;
    }

    try {
      setChartLoading(true);
      
      // Defensive check for yearRange
      const safeYearRange = Array.isArray(filters.yearRange) && filters.yearRange.length >= 2 
        ? filters.yearRange 
        : [2019, 2025]; // Default for trial version
      
      const params = new URLSearchParams({
        start_year: safeYearRange[0].toString(),
        end_year: safeYearRange[1].toString()
      });

      // Add therapeutic area parameter
      if (filters.therapeuticArea && filters.therapeuticArea !== 'All') {
        params.append('therapeutic_area', filters.therapeuticArea);
      }

      // Add region filter if specific regions are selected
      if (filters.region.length > 0) {
        filters.region.forEach(region => {
          params.append('regions', region);
        });
      }
      // If no regions selected, don't apply region filter (show all regions)

      // Fetch regional revenue data
      const response = await api.get(`/api/market/revenue-by-region?${params}`);
      console.log('REGIONAL DATA API RESPONSE:', response.data);
      
      const transformedData = transformChartData(response.data);
      console.log('TRANSFORMED DATA:', transformedData);
      setRegionalData(transformedData);
      
      // Calculate growth rates
      if (transformedData.length > 0) {
        const growthData = calculateGrowthRates(transformedData);
        console.log('CALCULATED GROWTH DATA:', growthData);
        setRegionalGrowth(growthData);
      } else {
        console.error('NO REGIONAL DATA RECEIVED - CLEARING ALL CHARTS');
        setRegionalData([]);
        setRegionalGrowth([]);
      }

    } catch (error) {
      console.error('ERROR FETCHING REGIONAL DATA:', error);
      // NO MOCK DATA - ONLY REAL DATABASE DATA
      console.error('FAILED TO FETCH REAL REGIONAL DATA - SHOWING EMPTY STATE');
      setRegionalData([]);
      setRegionalGrowth([]);
    } finally {
      setChartLoading(false);
    }
  }, [filters.yearRange, filters.region, filters.therapeuticArea]);

  // Fetch unfiltered data for global market share calculations
  const fetchGlobalData = React.useCallback(async () => {
    // Don't fetch if therapeutic area is not set yet
    if (!filters.therapeuticArea || filters.therapeuticArea === '') {
      console.log('Skipping fetchGlobalData - therapeutic area not set yet');
      return;
    }

    try {
      console.log('fetchGlobalData called with filters:', filters);
      console.log('filters.region:', filters.region);
      console.log('filters.region.length:', filters.region.length);
      
      const params = new URLSearchParams({
        start_year: (Array.isArray(filters.yearRange) ? filters.yearRange[0] : 2019).toString(),
        end_year: (Array.isArray(filters.yearRange) ? filters.yearRange[1] : 2025).toString()
      });

      // Add therapeutic area parameter
      if (filters.therapeuticArea && filters.therapeuticArea !== 'All') {
        params.append('therapeutic_area', filters.therapeuticArea);
      }
      
      // Fetch ALL regions without any filters for global market share calculation
      // This ensures we have the complete dataset to calculate accurate global percentages
      const response = await api.get(`/api/market/revenue-by-region?${params}`);
      const globalData = transformChartData(response.data);
      
      if (globalData.length > 0) {
        // Calculate global market shares using unfiltered data
        const globalShareData = calculateMarketShare(globalData);
        
        // Update market share with filtered regions but global percentages
        if (filters.region.length > 0) {
          // Filter to show only selected regions, but keep global market share percentages
          console.log('Available regions in global data:', globalShareData.map(item => item.name));
          console.log('Filtered regions requested:', filters.region);
          console.log('Global share data before filtering:', globalShareData);
          
          const filteredShareData = globalShareData.filter(item => {
            const isIncluded = filters.region.includes(item.name);
            console.log(`Region "${item.name}" in filters? ${isIncluded}`);
            return isIncluded;
          });
          
          console.log('Filtered market share data:', filteredShareData);
          setMarketShare(filteredShareData);
        } else {
          // No filters applied, show all regions with global percentages
          console.log('No region filters applied, showing all regions');
          console.log('Global market share data:', globalShareData);
          setMarketShare(globalShareData);
        }
      }
    } catch (error) {
      console.error('Error fetching global data for market share:', error);
    }
  }, [filters.yearRange, filters.region, filters.therapeuticArea]); // Add filters.region back to dependencies

  // Clear data when therapeutic area changes to prevent showing stale data
  useEffect(() => {
    setRegionalData([]);
    setRegionalGrowth([]);
    setMarketShare([]);
  }, [filters.therapeuticArea]);

  useEffect(() => {
    fetchRegionalData();
    fetchGlobalData();
  }, [fetchRegionalData, fetchGlobalData]);

  // Remove the separate useEffect for market share - let fetchGlobalData handle it directly
  // useEffect(() => {
  //   fetchGlobalData();
  // }, [fetchGlobalData, filters.region]);

  const transformChartData = (chartResponse: any) => {
    if (!chartResponse.series || chartResponse.series.length === 0) {
      console.log('No series data in response');
      return [];
    }

    console.log('Transforming chart data:', chartResponse);
    const dataByYear: any = {};
    chartResponse.series.forEach((series: any) => {
      if (!series.data) {
        console.warn('Series missing data:', series);
        return;
      }
      series.data.forEach((point: any) => {
        if (point && typeof point.x !== 'undefined' && typeof point.y !== 'undefined') {
          const year = point.x;
          if (!dataByYear[year]) {
            dataByYear[year] = { year };
          }
          dataByYear[year][series.name] = point.y;
        } else {
          console.warn('Invalid data point:', point);
        }
      });
    });

    const result = Object.values(dataByYear);
    console.log('Transformed data:', result);
    return result;
  };

  const calculateGrowthRates = (data: any[]) => {
    if (data.length < 2) return [];
    
    const regions = Object.keys(data[0]).filter(key => key !== 'year');
    const growthData = [];
    
    for (let i = 1; i < data.length; i++) {
      const yearData: any = { year: data[i].year };
      regions.forEach(region => {
        const current = data[i][region] || 0;
        const previous = data[i-1][region] || 0;
        const growth = previous > 0 ? ((current - previous) / previous) * 100 : 0;
        yearData[region] = growth;
      });
      growthData.push(yearData);
    }
    
    return growthData;
  };

  const calculateMarketShare = (data: any[]) => {
    if (data.length === 0) return [];
    
    const firstYear = data[0];
    const latestYear = data[data.length - 1];
    const regions = Object.keys(latestYear).filter(key => key !== 'year');
    
    // Calculate totals for both years
    const totalStart = regions.reduce((sum, region) => sum + (firstYear[region] || 0), 0);
    const totalEnd = regions.reduce((sum, region) => sum + (latestYear[region] || 0), 0);
    const years = latestYear.year - firstYear.year;
    
    return regions
      .map(region => {
        const revStart = firstYear[region] || 0;
        const revEnd = latestYear[region] || 0;
        const shareStart = totalStart > 0 ? (revStart / totalStart) * 100 : 0;
        const shareEnd = totalEnd > 0 ? (revEnd / totalEnd) * 100 : 0;
        const delta = revEnd - revStart;
        
        let cagr = 0;
        if (revStart > 0 && revEnd > 0 && years > 0) {
          cagr = (Math.pow(revEnd / revStart, 1 / years) - 1) * 100;
        }

        return {
          name: region,
          value: revEnd, // for compatibility
          share: shareEnd, // for compatibility
          revStart,
          revEnd,
          shareStart,
          shareEnd,
          delta,
          cagr
        };
      })
      .sort((a, b) => b.revEnd - a.revEnd); // Sort by market size (largest first)
  };

  // REMOVED ALL MOCK DATA FUNCTIONS - ONLY REAL DATABASE DATA ALLOWED
  // Market research companies cannot use fake data under any circumstances

  const safeYearRange = Array.isArray(filters.yearRange) && filters.yearRange.length >= 2 
    ? filters.yearRange 
    : [2019, 2025];
  const [startYear, endYear] = safeYearRange;

  const regionalTableColumns = [
    {
      title: 'Region',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: `Revenue (${startYear})`,
      dataIndex: 'revStart',
      key: 'revStart',
      render: (value: number) => formatRevenue(value),
      sorter: (a: any, b: any) => a.revStart - b.revStart,
    },
    {
      title: `Market Share (${startYear})`,
      dataIndex: 'shareStart',
      key: 'shareStart',
      render: (value: number) => `${value.toFixed(1)}%`,
      sorter: (a: any, b: any) => a.shareStart - b.shareStart,
    },
    {
      title: `Revenue (${endYear})`,
      dataIndex: 'revEnd',
      key: 'revEnd',
      render: (value: number) => formatRevenue(value),
      sorter: (a: any, b: any) => a.revEnd - b.revEnd,
      defaultSortOrder: 'descend' as const,
    },
    {
      title: `Market Share (${endYear})`,
      dataIndex: 'shareEnd',
      key: 'shareEnd',
      render: (value: number) => `${value.toFixed(1)}%`,
      sorter: (a: any, b: any) => a.shareEnd - b.shareEnd,
    },
    {
      title: `Δ$ (${startYear}→${endYear})`,
      dataIndex: 'delta',
      key: 'delta',
      render: (value: number) => (
        <span style={{ color: value >= 0 ? '#52c41a' : '#f5222d' }}>
          {formatDelta(value)}
        </span>
      ),
      sorter: (a: any, b: any) => a.delta - b.delta,
    },
    {
      title: `CAGR (${startYear}–${endYear})`,
      dataIndex: 'cagr',
      key: 'cagr',
      render: (value: number) => {
        if (startYear === endYear || value === 0) {
          return '–';
        }
        return (
          <span style={{ color: '#52c41a', fontWeight: 600 }}>
            {value.toFixed(1)}%
          </span>
        );
      },
      sorter: (a: any, b: any) => a.cagr - b.cagr,
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Loading regional analysis...</Text>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        {/* Market Share Distribution */}
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          <Card 
            title={
              <div style={{ margin: 0, padding: 0 }}>
                <Title level={5} style={{ margin: 0, marginBottom: 0 }}>Current Market Share</Title>
                <Text type="secondary" style={{ fontSize: '12px', margin: 0, display: 'block' }}>
                  Regional distribution of market
                </Text>
              </div>
            }
            loading={chartLoading}
            style={{ height: 460, display: 'flex', flexDirection: 'column' }}
            headStyle={{ padding: '12px 20px', minHeight: '72px', height: '72px' }}
            bodyStyle={{ padding: '20px' }}
          >
            <FullscreenChart
              chartId="market-share"
              title="Current Market Share"
              isFullscreen={fullscreenChart === 'market-share'}
              onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'market-share' ? null : 'market-share')}
              fullscreenContent={
                                 <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                    <Pie
                      data={marketShare}
                      cx="45%"
                      cy="50%"
                      innerRadius={80}
                      outerRadius={200}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, share, cx, cy, midAngle, outerRadius, payload }) => {
                        if (typeof midAngle === 'undefined' || typeof outerRadius === 'undefined' || typeof cx === 'undefined' || typeof cy === 'undefined') {
                          return null;
                        }
                        const RADIAN = Math.PI / 180;
                        const radius = outerRadius + 20;
                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                        const regionColor = REGION_COLORS[name] || COLORS[0];
                        return (
                          <text 
                            x={x} 
                            y={y} 
                            fill={regionColor}
                            textAnchor={x > cx ? 'start' : 'end'} 
                            dominantBaseline="central"
                            style={{
                                  fontFamily: getChartLabelStyle(isDark).fontFamily,
                                  fontSize: getChartLabelStyle(isDark).fontSize,
                                  fontWeight: getChartLabelStyle(isDark).fontWeight,
                                  letterSpacing: getChartLabelStyle(isDark).letterSpacing
                            }}
                          >
                            {`${name}: ${share.toFixed(1)}%`}
                          </text>
                        );
                      }}
                      labelLine={false}
                      animationBegin={0}
                      animationDuration={800}
                      animationEasing="ease-out"
                    >
                      {marketShare.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={REGION_COLORS[entry.name] || COLORS[index % COLORS.length]}
                          stroke="#fff"
                          strokeWidth={2}
                          style={{
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                            transition: 'opacity 0.3s'
                          }}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  </PieChart>
                </ResponsiveContainer>
              }
            >
              <ResponsiveContainer width="100%" height={340}>
              <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                <Pie
                  data={marketShare}
                  cx="45%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, share, cx, cy, midAngle, outerRadius }) => {
                    if (typeof midAngle === 'undefined' || typeof outerRadius === 'undefined' || typeof cx === 'undefined' || typeof cy === 'undefined') {
                      return null;
                    }
                    const RADIAN = Math.PI / 180;
                    const radius = outerRadius + 18;
                    const x = cx + radius * Math.cos(-midAngle * RADIAN);
                    const y = cy + radius * Math.sin(-midAngle * RADIAN);
                    const regionColor = REGION_COLORS[name] || COLORS[0];
                    return (
                      <text 
                        x={x} 
                        y={y} 
                        fill={regionColor}
                        textAnchor={x > cx ? 'start' : 'end'} 
                        dominantBaseline="central"
                        style={{
                                  fontFamily: getChartLabelStyle(isDark).fontFamily,
                                  fontSize: getChartLabelStyle(isDark).fontSize,
                                  fontWeight: getChartLabelStyle(isDark).fontWeight,
                                  letterSpacing: getChartLabelStyle(isDark).letterSpacing
                        }}
                      >
                        {`${name}: ${share.toFixed(1)}%`}
                      </text>
                    );
                  }}
                  labelLine={false}
                  animationBegin={0}
                  animationDuration={800}
                  animationEasing="ease-out"
                >
                  {marketShare.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={REGION_COLORS[entry.name] || COLORS[index % COLORS.length]}
                      stroke="#fff"
                      strokeWidth={2}
                      style={{
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                        transition: 'opacity 0.3s'
                      }}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip isDark={isDark} />} />
              </PieChart>
            </ResponsiveContainer>
            </FullscreenChart>
          </Card>
        </Col>

        {/* World Map Analysis */}
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          <Card 
            title={
              <div style={{ margin: 0, padding: 0 }}>
                <Title level={5} style={{ margin: 0, marginBottom: 0 }}>Global Regional Coverage</Title>
                <Text type="secondary" style={{ fontSize: '12px', margin: 0, display: 'block' }}>
                  Interactive world map showing regional distribution
                </Text>
              </div>
            }
            style={{ height: 460, display: 'flex', flexDirection: 'column' }}
            headStyle={{ padding: '12px 20px', minHeight: '72px', height: '72px' }}
            bodyStyle={{ padding: '8px', flex: 1 }}
          >
            <FullscreenChart
              chartId="world-map"
              title="Global Regional Coverage"
              isFullscreen={fullscreenChart === 'world-map'}
              onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'world-map' ? null : 'world-map')}
              fullscreenContent={
                <div style={{ width: '100%', height: '100%' }}>
                  <WorldMap selectedRegions={filters.region} regionalData={marketShare} />
                </div>
              }
            >
              <WorldMap selectedRegions={filters.region} regionalData={marketShare} compact />
            </FullscreenChart>
          </Card>
        </Col>

        {/* Regional Revenue Trends */}
        <Col span={24}>
          <Card 
            className="market-overview-card"
            title={
              <div>
                <Title level={5} style={{ margin: 0 }}>Regional Revenue Trends</Title>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '12px', 
                  marginTop: '12px',
                  width: '100%'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    justifyContent: 'flex-start',
                    width: '100%',
                    minHeight: '36px',
                    paddingTop: '4px'
                  }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>View:</Text>
                    <Segmented
                      size="small"
                      className="fit-content-segmented"
                      options={[
                        { label: 'Bars', value: 'bars' },
                        { label: 'Area', value: 'area' }
                      ]}
                      value={chartView}
                      onChange={(value) => setChartView(value as 'bars' | 'area')}
                    />
                  </div>
                </div>
              </div>
            }
            loading={chartLoading}
          >
            <FullscreenChart
              chartId="regional-revenue"
              title="Regional Revenue Trends"
              isFullscreen={fullscreenChart === 'regional-revenue'}
              onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'regional-revenue' ? null : 'regional-revenue')}
              fullscreenContent={
                <ResponsiveContainer width="100%" height="100%">
                  {chartView === 'bars' ? (
                    <BarChart data={regionalData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" tick={{ style: getChartFontStyle(isDark) }} />
                      <YAxis tick={{ style: getChartFontStyle(isDark) }} />
                      <Tooltip 
                        formatter={(value: any, name: string) => [`$${Number(value).toFixed(1)}M`, name]}
                        labelFormatter={(label) => `Year: ${label}`}
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
                            
                            // Sort payload by value in descending order for consistent tooltip display
                            const sortedPayload = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
                            
                            return (
                              <div style={{
                                backgroundColor: isDark ? '#1f1f1f' : '#fff',
                                border: `1px solid ${isDark ? '#434343' : '#ccc'}`,
                                borderRadius: '4px',
                                padding: '8px',
                                boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.15)'
                              }}>
                                <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#333' }}>Year: {label}</p>
                                {sortedPayload.map((entry, index) => (
                                  <p key={index} style={{ 
                                    margin: '4px 0', 
                                    color: entry.color,
                                    fontSize: '12px'
                                  }}>
                                    {entry.name}: ${Number(entry.value).toFixed(1)}M
                                  </p>
                                ))}
                                <p style={{ 
                                  margin: '8px 0 0 0', 
                                  fontWeight: 'bold',
                                  borderTop: `1px solid ${isDark ? '#434343' : '#eee'}`,
                                  paddingTop: '8px',
                                  color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#333'
                                }}>
                                  Total: ${total.toFixed(1)}M
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      {regionalData.length > 0 &&
                        getSortedRegions().map((region, index) => (
                          <Bar
                            key={region}
                            dataKey={region}
                            fill={REGION_COLORS[region] || COLORS[index % COLORS.length]}
                          />
                        ))
                      }
                      <Legend wrapperStyle={legendStyle} />
                    </BarChart>
                  ) : (
                    <AreaChart data={regionalData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" tick={{ style: getChartFontStyle(isDark) }} />
                      <YAxis tick={{ style: getChartFontStyle(isDark) }} />
                      <Tooltip 
                        formatter={(value: any, name: string) => [`$${Number(value).toFixed(1)}M`, name]}
                        labelFormatter={(label) => `Year: ${label}`}
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
                            
                            // Sort payload by value in descending order for consistent tooltip display
                            const sortedPayload = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
                            
                            return (
                              <div style={{
                                backgroundColor: isDark ? '#1f1f1f' : '#fff',
                                border: `1px solid ${isDark ? '#434343' : '#ccc'}`,
                                borderRadius: '4px',
                                padding: '8px',
                                boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.15)'
                              }}>
                                <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#333' }}>Year: {label}</p>
                                {sortedPayload.map((entry, index) => (
                                  <p key={index} style={{ 
                                    margin: '4px 0', 
                                    color: entry.color,
                                    fontSize: '12px'
                                  }}>
                                    {entry.name}: ${Number(entry.value).toFixed(1)}M
                                  </p>
                                ))}
                                <p style={{ 
                                  margin: '8px 0 0 0', 
                                  fontWeight: 'bold',
                                  borderTop: `1px solid ${isDark ? '#434343' : '#eee'}`,
                                  paddingTop: '8px',
                                  color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#333'
                                }}>
                                  Total: ${total.toFixed(1)}M
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                     {regionalData.length > 0 &&
                       getReversedRegions().map((region, index) => (
                         <Area
                           key={region}
                           type="monotone"
                           dataKey={region}
                           stackId="a"
                           stroke={REGION_COLORS[region] || COLORS[index % COLORS.length]}
                           fill={REGION_COLORS[region] || COLORS[index % COLORS.length]}
                           fillOpacity={0.8}
                         />
                       ))
                     }
                      <Legend wrapperStyle={legendStyle} />
                    </AreaChart>
                  )}
                </ResponsiveContainer>
              }
            >
              <ResponsiveContainer width="100%" height={300}>
                {chartView === 'bars' ? (
                  <BarChart data={regionalData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" tick={{ style: getChartFontStyle(isDark) }} />
                    <YAxis tick={{ style: getChartFontStyle(isDark) }} />
                    <Tooltip 
                      formatter={(value: any, name: string) => [`$${Number(value).toFixed(1)}M`, name]}
                      labelFormatter={(label) => `Year: ${label}`}
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
                          
                          // Sort payload by value in descending order for consistent tooltip display
                          const sortedPayload = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
                          
                          return (
                            <div style={{
                              backgroundColor: isDark ? '#1f1f1f' : '#fff',
                              border: `1px solid ${isDark ? '#434343' : '#ccc'}`,
                              borderRadius: '4px',
                              padding: '8px',
                              boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.15)'
                            }}>
                              <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#333' }}>Year: {label}</p>
                              {sortedPayload.map((entry, index) => (
                                <p key={index} style={{ 
                                  margin: '4px 0', 
                                  color: entry.color,
                                  fontSize: '12px'
                                }}>
                                  {entry.name}: ${Number(entry.value).toFixed(1)}M
                                </p>
                              ))}
                              <p style={{ 
                                margin: '8px 0 0 0', 
                                fontWeight: 'bold',
                                borderTop: `1px solid ${isDark ? '#434343' : '#eee'}`,
                                paddingTop: '8px',
                                color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#333'
                              }}>
                                Total: ${total.toFixed(1)}M
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    {regionalData.length > 0 &&
                      getSortedRegions().map((region, index) => (
                        <Bar
                          key={region}
                          dataKey={region}
                          fill={REGION_COLORS[region] || COLORS[index % COLORS.length]}
                        />
                      ))
                    }
                    <Legend wrapperStyle={legendStyle} />
                  </BarChart>
                ) : (
                  <AreaChart data={regionalData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" tick={{ style: getChartFontStyle(isDark) }} />
                    <YAxis tick={{ style: getChartFontStyle(isDark) }} />
                    <Tooltip 
                      formatter={(value: any, name: string) => [`$${Number(value).toFixed(1)}M`, name]}
                      labelFormatter={(label) => `Year: ${label}`}
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
                          
                          // Sort payload by value in descending order for consistent tooltip display
                          const sortedPayload = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
                          
                          return (
                            <div style={{
                              backgroundColor: isDark ? '#1f1f1f' : '#fff',
                              border: `1px solid ${isDark ? '#434343' : '#ccc'}`,
                              borderRadius: '4px',
                              padding: '8px',
                              boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.15)'
                            }}>
                              <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#333' }}>Year: {label}</p>
                              {sortedPayload.map((entry, index) => (
                                <p key={index} style={{ 
                                  margin: '4px 0', 
                                  color: entry.color,
                                  fontSize: '12px'
                                }}>
                                  {entry.name}: ${Number(entry.value).toFixed(1)}M
                                </p>
                              ))}
                              <p style={{ 
                                margin: '8px 0 0 0', 
                                fontWeight: 'bold',
                                borderTop: `1px solid ${isDark ? '#434343' : '#eee'}`,
                                paddingTop: '8px',
                                color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#333'
                              }}>
                                Total: ${total.toFixed(1)}M
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                     {regionalData.length > 0 &&
                       getReversedRegions().map((region, index) => (
                         <Area
                           key={region}
                           type="monotone"
                           dataKey={region}
                           stackId="a"
                           stroke={REGION_COLORS[region] || COLORS[index % COLORS.length]}
                           fill={REGION_COLORS[region] || COLORS[index % COLORS.length]}
                           fillOpacity={0.8}
                         />
                       ))
                     }
                    <Legend wrapperStyle={legendStyle} />
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </FullscreenChart>
          </Card>
        </Col>


        {/* Regional Growth Rates */}
        <Col span={24}>
          <Card 
            title={
              <div style={{ margin: 0, padding: 0 }}>
                <Title level={5} style={{ margin: 0, marginBottom: 0 }}>Regional Growth Rates</Title>
                <Text type="secondary" style={{ fontSize: '12px', margin: 0, display: 'block' }}>
                  Year-over-year growth by region
                </Text>
              </div>
            }
            loading={chartLoading}
            headStyle={{ padding: '12px 20px' }}
            bodyStyle={{ padding: '20px' }}
          >
            <FullscreenChart
              chartId="regional-growth"
              title="Regional Growth Rates"
              isFullscreen={fullscreenChart === 'regional-growth'}
              onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'regional-growth' ? null : 'regional-growth')}
              fullscreenContent={
                                 <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={regionalGrowth}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" tick={{ style: getChartFontStyle(isDark) }} />
                    <YAxis tick={{ style: getChartFontStyle(isDark) }} />
                    <Tooltip 
                      formatter={(value: any, name: string) => [`${value.toFixed(1)}%`, name]}
                      labelFormatter={(label) => `Year: ${label}`}
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          // Sort payload by value in descending order for consistent tooltip display
                          const sortedPayload = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
                          
                          return (
                            <div style={{
                              backgroundColor: isDark ? '#1f1f1f' : '#fff',
                              border: `1px solid ${isDark ? '#434343' : '#ccc'}`,
                              borderRadius: '4px',
                              padding: '8px',
                              boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.15)'
                            }}>
                              <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#333' }}>Year: {label}</p>
                              {sortedPayload.map((entry, index) => (
                                <p key={index} style={{ 
                                  margin: '4px 0', 
                                  color: entry.color,
                                  fontSize: '12px'
                                }}>
                                  {entry.name}: {Number(entry.value).toFixed(1)}%
                                </p>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    {regionalGrowth.length > 0 && 
                      Object.keys(regionalGrowth[0] || {})
                        .filter(key => key !== 'year')
                        .map((region, index) => (
                          <Line 
                            key={region}
                            type="monotone"
                            dataKey={region} 
                            stroke={REGION_COLORS[region] || COLORS[index % COLORS.length]}
                            strokeWidth={3}
                          />
                        ))
                    }
                    <Legend wrapperStyle={legendStyle} />
                  </LineChart>
                </ResponsiveContainer>
              }
            >
              <ResponsiveContainer width="100%" height={300}>
              <LineChart data={regionalGrowth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" tick={{ style: getChartFontStyle(isDark) }} />
                <YAxis tick={{ style: getChartFontStyle(isDark) }} />
                <Tooltip 
                  formatter={(value: any, name: string) => [`${value.toFixed(1)}%`, name]}
                  labelFormatter={(label) => `Year: ${label}`}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      // Sort payload by value in descending order for consistent tooltip display
                      const sortedPayload = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
                      
                      return (
                        <div style={{
                          backgroundColor: isDark ? '#1f1f1f' : '#fff',
                          border: `1px solid ${isDark ? '#434343' : '#ccc'}`,
                          borderRadius: '4px',
                          padding: '8px',
                          boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.15)'
                        }}>
                          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#333' }}>Year: {label}</p>
                          {sortedPayload.map((entry, index) => (
                            <p key={index} style={{ 
                              margin: '4px 0', 
                              color: entry.color,
                              fontSize: '12px'
                            }}>
                              {entry.name}: {Number(entry.value).toFixed(1)}%
                            </p>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                {regionalGrowth.length > 0 && 
                  // ONLY REAL DATABASE DATA - Dynamically render lines for all regions in the data
                  Object.keys(regionalGrowth[0] || {})
                    .filter(key => key !== 'year')
                    .map((region, index) => (
                      <Line 
                        key={region}
                        type="monotone"
                        dataKey={region} 
                        stroke={REGION_COLORS[region] || COLORS[index % COLORS.length]}
                        strokeWidth={3}
                      />
                    ))
                }
                <Legend wrapperStyle={legendStyle} />
              </LineChart>
            </ResponsiveContainer>
            </FullscreenChart>
          </Card>
        </Col>

        {/* Regional Summary Table */}
        <Col span={24}>
          <Card 
            title={
              <div>
                <Title level={5} style={{ margin: 0 }}>Regional Summary</Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Detailed regional performance metrics across selected period
                </Text>
              </div>
            }
            loading={chartLoading}
            headStyle={{ padding: '16px 20px' }}
            bodyStyle={{ padding: '20px' }}
          >
            <FullscreenChart
              chartId="regional-summary"
              title="Regional Summary"
              isFullscreen={fullscreenChart === 'regional-summary'}
              onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'regional-summary' ? null : 'regional-summary')}
              fullscreenContent={
                <Table
                  columns={regionalTableColumns}
                  dataSource={marketShare}
                  rowKey="name"
                  pagination={false}
                  size="large"
                  sortDirections={['descend', 'ascend']}
                />
              }
            >
              <Table
              columns={regionalTableColumns}
              dataSource={marketShare}
              rowKey="name"
              pagination={false}
              size="middle"
              sortDirections={['descend', 'ascend']}
            />
            </FullscreenChart>
          </Card>
        </Col>
      </Row>

      {/* Regional Insights */}
      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col span={24}>
          <Card title="Regional Insights">
            <Row gutter={[16, 16]}>
              {marketShare.length > 0 && regionalGrowth.length > 0 ? (
                // ONLY REAL DATABASE DATA - NO FALLBACKS EVER
                <>
                  <Col span={6}>
                    <div style={{ textAlign: 'center', padding: '16px' }}>
                      <Title level={4} style={{ color: '#1890ff', margin: 0 }}>
                        {(() => {
                          // Calculate proper CAGR for each region and find the fastest
                          if (regionalData.length < 2) return 'N/A';
                          
                          const firstYear = regionalData[0];
                          const lastYear = regionalData[regionalData.length - 1];
                          const regions = Object.keys(firstYear).filter(key => key !== 'year');
                          const years = lastYear.year - firstYear.year;
                          
                          if (years === 0) return 'N/A';
                          
                          const regionCAGRs = regions.map(region => {
                            const startValue = firstYear[region] || 0;
                            const endValue = lastYear[region] || 0;
                            if (startValue <= 0) return 0;
                            return (Math.pow(endValue / startValue, 1 / years) - 1) * 100;
                          });
                          
                          const fastestCAGR = Math.max(...regionCAGRs);
                          return fastestCAGR.toFixed(1);
                        })()}%
                      </Title>
                      <Text type="secondary">Fastest Growth Rate</Text>
                    </div>
                  </Col>
                  <Col span={6}>
                    <div style={{ textAlign: 'center', padding: '16px' }}>
                      <Title level={4} style={{ color: '#52c41a', margin: 0 }}>
                        {(() => {
                          // Find region with highest CAGR
                          if (regionalData.length < 2) return 'N/A';
                          
                          const firstYear = regionalData[0];
                          const lastYear = regionalData[regionalData.length - 1];
                          const regions = Object.keys(firstYear).filter(key => key !== 'year');
                          const years = lastYear.year - firstYear.year;
                          
                          if (years === 0) return 'N/A';
                          
                          let fastestRegion = '';
                          let fastestCAGR = -Infinity;
                          
                          regions.forEach(region => {
                            const startValue = firstYear[region] || 0;
                            const endValue = lastYear[region] || 0;
                            if (startValue > 0) {
                              const cagr = (Math.pow(endValue / startValue, 1 / years) - 1) * 100;
                              if (cagr > fastestCAGR) {
                                fastestCAGR = cagr;
                                fastestRegion = region;
                              }
                            }
                          });
                          
                          return fastestRegion || 'N/A';
                        })()}
                      </Title>
                      <Text type="secondary">Fastest Growing</Text>
                    </div>
                  </Col>
                  <Col span={6}>
                    <div style={{ textAlign: 'center', padding: '16px' }}>
                      <Title level={4} style={{ color: '#faad14', margin: 0 }}>
                        {Array.isArray(filters.yearRange) ? `${filters.yearRange[0]}-${filters.yearRange[1]}` : '2019-2025'}
                      </Title>
                      <Text type="secondary">Analysis Period</Text>
                    </div>
                  </Col>
                  <Col span={6}>
                    <div style={{ textAlign: 'center', padding: '16px' }}>
                      <Title level={4} style={{ color: '#722ed1', margin: 0 }}>
                        {(() => {
                          // Calculate proper CAGR using first and last year data
                          if (regionalData.length < 2) return 'N/A';
                          
                          const firstYear = regionalData[0];
                          const lastYear = regionalData[regionalData.length - 1];
                          const regions = Object.keys(firstYear).filter(key => key !== 'year');
                          const years = lastYear.year - firstYear.year;
                          
                          if (years === 0) return 'N/A';
                          
                          const regionCAGRs = regions.map(region => {
                            const startValue = firstYear[region] || 0;
                            const endValue = lastYear[region] || 0;
                            if (startValue <= 0) return 0;
                            return (Math.pow(endValue / startValue, 1 / years) - 1) * 100;
                          });
                          
                          const avgCAGR = regionCAGRs.reduce((sum, cagr) => sum + cagr, 0) / regionCAGRs.length;
                          return avgCAGR.toFixed(1);
                        })()}%
                      </Title>
                      <Text type="secondary">Average CAGR</Text>
                    </div>
                  </Col>
                </>
              ) : (
                // NO DATA AVAILABLE - SHOW NOTHING OR LOADING STATE
                <Col span={24}>
                  <div style={{ textAlign: 'center', padding: '32px' }}>
                    <Text type="secondary">Loading regional insights...</Text>
                  </div>
                </Col>
              )}
            </Row>
          </Card>
        </Col>
      </Row>


    </div>
  );
};

export default RegionalAnalysis;